import $ from "jquery";
import * as d3 from "d3";

import { initCanvas, plannerInitGlobals } from 'src/components/Planner/planner/planner-init';
import { GetFloorRoomsAndRelatedObjectsResponse } from 'src/model/api/v2/data-contracts';
import { PlannerGlobals } from 'src/components/Planner/planner/planner-globals';
import { drawDevSymbols, getViewBoxElem } from 'src/components/Planner/planner/planner';
import { redrawNotes } from 'src/components/Planner/planner/notes';
import { uDev } from 'src/components/Planner/planner/uDev';
import { makeSVG, svgToRasterImageNode } from 'src/components/Planner/planner/uMath';
import { deviceProvider } from 'src/components/Planner/planner/DeviceProvider';
import { floorProvider } from 'src/components/Planner/planner/FloorProvider';
import { initController } from 'src/components/Planner/controller';
import { calcAndShowWifiHeatmap, initWifiCalc, removeWifiHeatmap } from 'src/components/Planner/planner/wificalc-dom';
import { initMovsen, isCamera, toggleMotionSensorsMode } from 'src/components/Planner/planner/movsen';
import { getAPs, setWifiBand, WIFI_BANDS, WIFICALC } from 'src/components/Planner/planner/wificalc';
import { deepCopy } from 'src/utils/controllerAdapterForOfferPage';
import { GlobalsVariables } from 'src/utils/constants';

export interface FloorData {
  projectId: number,
  floorId: number,
  floorHTML: string,
  floorPlan: GetFloorRoomsAndRelatedObjectsResponse
}

export interface PlannerInitBundle {
  devicesData: any,
  myProductsData: any,
  floorsData: any,
  env: string,
  userRoleInProject: string,
}

export interface PlannerSvgEntry {
  plannerSVG: any,
  floorId: number,
  isWifi?: boolean,
  wifiType?: string,
  isCamera?: boolean,
  isWiring?: boolean,
  wiringType?: string,
  legend?: any,
  cachedFloor?: any,
  floorHTML?: string,
}

export interface RoomSvgEntry {
  roomId: number,
  floorId: number,
  boundingBox: any,
  roomSVG: any,
  cachedRoom?: any,
}

export interface OfferSvgData {
  plannerSVGs: PlannerSvgEntry[],
  roomSVGs: RoomSvgEntry[],
  roomProps: { [roomId: number]: { area: number | null, airspace: number | null } },
  gatheredGroups: any[],
  gatheredRooms: { [roomId: number]: any },
  gatheredLengthOf1Px: { [floorId: number]: any },
  gatheredFloorIdToRooms: { [floorId: number]: any },
  "signal-legend"?: any,
}

const noop = () => {};
const INTERACTION_NOOPS = new Array(12).fill(noop);

// frontend/web/js/app_scripts/docu/utils/utils.js.
const EXPORT_PAGE_WIDTH_MM = 240;
const EXPORT_PAGE_HEIGHT_MM = 297;
const EXPORT_LANDSCAPE_PAGE_WIDTH_MM = 210;
const EXPORT_LANDSCAPE_PAGE_HEIGHT_MM = 310;

/**
 * Returns the offer document's HTML for the PDF export. wkhtmltopdf's WebKit
 * cannot size an SVG from its view-box, so the floor SVGs - laid out with
 * width: auto on screen - are rendered with zero height in the PDF and only
 * their place is left. Ported from the legacy DOCU_HELPER.postTasks.
 */
export function getOfferExportHtml(root: HTMLElement): string {
  const clone = $(root).clone();

  clone.find("svg.quote-floorSVG").each(function (this: any) {
    fitFloorSvgToPage($(this));
  });

  return clone.html();
}

function fitFloorSvgToPage($svg: any) {
  const isLandscape = rotateLandscapeFloorSvg($svg);

  $svg
      .attr("width", `${isLandscape ? EXPORT_LANDSCAPE_PAGE_WIDTH_MM : EXPORT_PAGE_WIDTH_MM}mm`)
      .attr("height", `${isLandscape ? EXPORT_LANDSCAPE_PAGE_HEIGHT_MM : EXPORT_PAGE_HEIGHT_MM}mm`)
      .css({ width: "", maxHeight: "" });

  $svg.closest(".map-center-align").css("text-align", "center");

  const $mapRoot = $svg.closest(".map-comp-root");
  addExportHeaderSpacing($mapRoot);

  if (isLandscape) {
    rotateLandscapeOverlays($svg, $mapRoot);
  }
}

/**
 * Turns a landscape floor plan sideways so it fills the portrait page, by
 * swapping the view-box axes and rotating the drawing inside it. Returns
 * whether the plan was landscape.
 */
function rotateLandscapeFloorSvg($svg: any): boolean {
  const viewBox = $svg.attr("viewBox");
  if (!viewBox || typeof viewBox !== "string") {
    return false;
  }

  const [x, y, width, height] = viewBox.split(" ").map((value: string) => +value);
  if (!(width > height)) {
    return false;
  }

  const $gElem = $svg.children("g");
  if (!$gElem.length) {
    return false;
  }

  $svg.attr("viewBox", [y, x, height, width].join(" "));
  $gElem.wrap($(makeSVG("g")).attr("transform", "rotate(90,450,450)"));

  return true;
}

function addExportHeaderSpacing($mapRoot: any) {
  const candidates = [
    $mapRoot.find("h2, h3").first(),
    $mapRoot.prev("h2, h3").first(),
    $mapRoot.prev(".floor-header-container").find("h2, h3").first(),
  ];

  candidates
      .find(($candidate) => $candidate.length)
      ?.css({ marginTop: "15px", marginBottom: "15px" });
}

function rotateLandscapeOverlays($svg: any, $mapRoot: any) {
  const $infoBox = $svg.closest(".quote-map").find(".floormap-info-box");
  if ($infoBox.length) {
    $infoBox.css({ right: "60px", bottom: "10px", top: "" });
    appendStyle($infoBox, "-webkit-transform: rotate(90deg); -webkit-transform-origin: right;");
  }

  const $signalLegend = $mapRoot.find(".signal-legend");
  if ($signalLegend.length) {
    appendStyle(
        $signalLegend,
        "-webkit-transform: rotate(90deg)translateX(335mm); -webkit-transform-origin-x: right; -webkit-transform-origin-y: top;",
    );
    $signalLegend.next("span").before("<br>");
  }
}

/**
 * Appends raw css text to the style attribute. The vendor prefix is what
 * wkhtmltopdf's WebKit understands, and setting it through jQuery's .css()
 * would let the browser rewrite it to the unprefixed property.
 */
function appendStyle($element: any, css: string) {
  const element = $element[0];
  element.setAttribute("style", `${element.getAttribute("style") || ""} ${css}`);
}

/**
 * Reuses the planner's own quote view-box cache (populated in initCanvas when
 * isInQuote). Resolves the view-box for a floor by reading the natural size of
 * the rendered floor background image.
 */
export function getSVGViewBox(jqSVG: any, floorId: number): Promise<string> {
  return new Promise((resolve) => {
    const cache: any = (PlannerGlobals.quoteViewBoxCache = PlannerGlobals.quoteViewBoxCache || {});
    if (floorId in cache) {
      return resolve(cache[floorId]);
    }
    const src = jqSVG.find(".plannerImage").attr("xlink:href");
    if (!src) {
      return resolve("");
    }
    const virtualImg = new Image();
    virtualImg.src = src;
    virtualImg.onload = function () {
      const vb = getViewBoxElem(virtualImg);
      cache[floorId] = vb;
      return resolve(vb);
    };
    virtualImg.onerror = () => resolve("");
  });
}

/**
 * Renders each floor of the project off-screen with the (read-only) planner in
 * quote mode, cloning the produced SVG (floor plan, per-room crops, wifi 2.4/5GHz,
 * camera coverage and wiring overlays) for the offer document. Ported from the
 * legacy DOCU_HELPER.loadPlanners / loadPlannersRecursive / loadWiringMaps.
 */
export async function getAndTransformSvgData(
    projectId: number,
    floorDatas: FloorData[],
    routingSvgs: string[],
    initBundle: PlannerInitBundle,
): Promise<OfferSvgData> {
  const target: OfferSvgData = {
    plannerSVGs: [],
    roomSVGs: [],
    roomProps: {},
    gatheredGroups: [],
    gatheredRooms: {},
    gatheredLengthOf1Px: {},
    gatheredFloorIdToRooms: {},
  };

  const host = $('#fake-maincontainer');
  $("#quote-planner").remove();
  host.append('<div id="quote-planner"></div>');
  $("#quote-planner").height(0).css("overflow", "hidden");

  // One-time planner bootstrap (mirrors Planner.tsx before initCanvas).
  GlobalsVariables.env = initBundle.env;
  initController();
  initWifiCalc();
  initMovsen();
  plannerInitGlobals();
  deviceProvider.setDevices(initBundle.devicesData, initBundle.myProductsData);
  floorProvider.setFloors(initBundle.floorsData);
  PlannerGlobals.quoteViewBoxCache = PlannerGlobals.quoteViewBoxCache || {};

  for (const floorData of floorDatas) {
    await renderFloor(target, floorData, projectId, initBundle.userRoleInProject);
  }

  await loadWiringMaps(target, routingSvgs);

  return target;
}

function renderFloor(
    target: OfferSvgData,
    floorData: FloorData,
    projectId: number,
    userRoleInProject: string,
): Promise<void> {
  return new Promise((resolve) => {
    const floorId = floorData.floorId;
    const tempCont = $("#quote-planner");
    // Native innerHTML (not jQuery .html()) so the floor partial's <script> tags
    // are NOT executed — we only need its DOM scaffold; the planner code is bundled.
    const contEl = tempCont[0];
    if (contEl) {
      contEl.innerHTML = floorData.floorHTML;
    }

    if (!target["signal-legend"]) {
      target["signal-legend"] = $(".signal-legend").clone();
    }

    const onDrawn = () => {
      try {
        collectFloorSvg(target, floorId, tempCont);
      } finally {
        resolve();
      }
    };

    initCanvas(
        floorId,
        onDrawn,
        () => resolve(),
        projectId,
        userRoleInProject,
        deepCopy(floorData.floorPlan),
        ...INTERACTION_NOOPS,
    );
  });
}

function collectFloorSvg(target: OfferSvgData, floorId: number, tempCont: any) {
  PlannerGlobals.symbols_on = true;
  PlannerGlobals.notes_on = false;
  drawDevSymbols();

  PlannerGlobals.symbols_on = false;
  PlannerGlobals.notes_on = true;
  drawDevSymbols();
  redrawNotes();

  $("#notes").removeAttr("id").addClass("planner-notes");
  d3.select("#plannerIMAGE").attr("class", "plannerImage");
  d3.selectAll(".rooms > polygon").style("fill", "white");
  d3.selectAll(".roomfaders > polygon").style("fill", "transparent");

  PlannerGlobals.groups.forEach((grp: any) => target.gatheredGroups.push(grp));
  PlannerGlobals.rooms.forEach((room: any) => (target.gatheredRooms[room.id] = room));
  target.gatheredLengthOf1Px[PlannerGlobals.plannerFloorId as any] = PlannerGlobals.LENGTHOF1PX;
  target.gatheredFloorIdToRooms[PlannerGlobals.plannerFloorId as any] = PlannerGlobals.rooms;

  const _24ghz = computeWifiClone(WIFI_BANDS._24, tempCont);
  const _5ghz = computeWifiClone(WIFI_BANDS._5, tempCont);

  d3.selectAll(".poly").each(function (this: any, _d: any, i: number) {
    const roomProps: any = {
      roomId: +$(this).attr("data-roomId")!,
      floorId: floorId,
      boundingBox: this.getBBox(),
      roomSVG: undefined,
    };

    // Once per floor: clone the whole rendered floor SVG.
    if (i === 0) {
      const woWifi = tempCont.find("#plannerSVG").clone();
      woWifi
          .find("> g")
          .first()
          .attr("id", `floor_g_${roomProps.floorId}`)
          .addClass("floor_g");
      target.plannerSVGs.push({
        plannerSVG: woWifi,
        floorId: floorId,
        isWifi: false,
      });

      if (_24ghz && _5ghz) {
        target.plannerSVGs.push({ plannerSVG: _24ghz, floorId, isWifi: true, wifiType: "2.4GHz" });
        target.plannerSVGs.push({ plannerSVG: _5ghz, floorId, isWifi: true, wifiType: "5GHz" });
      }

      collectCameraSvg(target, floorId, tempCont);
    }

    const ROOMVIEW_MARGIN = roomProps.boundingBox.width * 0.1;
    if (
        roomProps.roomId !== uDev.OUTDOORS_ROOM_ID &&
        roomProps.roomId !== uDev.UNASSIGNED_ROOM_ID
    ) {
      roomProps.boundingBox.x -= ROOMVIEW_MARGIN;
      roomProps.boundingBox.y -= ROOMVIEW_MARGIN;
      roomProps.boundingBox.width += ROOMVIEW_MARGIN * 2;
      roomProps.boundingBox.height += ROOMVIEW_MARGIN * 2;
    }

    roomProps.roomSVG = $(makeSVG("svg"));
    roomProps.roomSVG.attr("data-floorid", floorId);
    roomProps.roomSVG
        .attr("width", "100%")
        .attr("height", "500px")
        .attr(
            "viewBox",
            `${roomProps.boundingBox.x} ${roomProps.boundingBox.y} ${roomProps.boundingBox.width} ${roomProps.boundingBox.height} `,
        );
    roomProps.roomSVG.append(`<use xlink:href="#floor_g_${roomProps.floorId}"></use>`);
    $(makeSVG("g")).attr("class", "roomfaders").appendTo(roomProps.roomSVG);

    // Fade out the other rooms on the zoomed room map.
    tempCont
        .find("#plannerSVG")
        .find(".poly")
        .not(`[data-roomId="${roomProps.roomId}"]`)
        .not(`[data-roomId="${uDev.OUTDOORS_ROOM_ID}"]`)
        .not(`[data-roomId="${uDev.UNASSIGNED_ROOM_ID}"]`)
        .each(function (this: any) {
          const $this = $(this);
          const clone = $this.clone();
          $this.attr("stroke", "#8ee6da");
          $this.css("fill-opacity", "0.7");
          clone.attr("stroke", "");
          clone.css("fill-opacity", 0.85);
          roomProps.roomSVG.find(".roomfaders").append(clone);
        });

    target.roomSVGs.push(roomProps);
  });

  PlannerGlobals.rooms.forEach((room: any) => {
    target.roomProps[room.id] = {
      area:
          room.room_area && room.room_area !== 0 ? room.room_area / 10000 : null,
      airspace:
          room.room_airspace && room.room_airspace !== 0 ? room.room_airspace : null,
    };
  });
}

/**
 * Renders the wifi heatmap for one band into the live planner, rasterizes it to
 * an <image> and returns a clone of the resulting floor SVG. Returns null when
 * there are no access points on the floor. Ported from legacy loadPlannersRecursive.
 */
function computeWifiClone(band: any, tempCont: any): any {
  setWifiBand(band);
  calcAndShowWifiHeatmap();
  const canvas: any = document.querySelector(".heatmap-canvas");
  if (!canvas) {
    return null;
  }
  const canvSize = canvas.width;
  const canvAdj = (900 - canvSize) / 2;
  const APs = getAPs();
  let clone: any = null;
  if (APs.length) {
    const url = canvas.toDataURL();
    d3.select("#containerSVG")
        .append("svg:image")
        .attr("class", "heatmap-img")
        .attr("x", canvAdj)
        .attr("y", canvAdj)
        .attr("width", canvSize)
        .attr("height", canvSize)
        .attr("xlink:href", url);
    $(".heatmap-canvas").closest("foreignObject").remove();
    clone = tempCont.find("#plannerSVG").clone();
  }
  removeWifiHeatmap();
  WIFICALC.reset();
  d3.select(".heatmap-img").remove();
  return clone;
}

/**
 * Produces the camera-coverage floor SVG (only camera devices kept) and splices
 * it right after the base floor entry. Ported from legacy loadPlannersRecursive.
 */
function collectCameraSvg(target: OfferSvgData, floorId: number, tempCont: any) {
  toggleMotionSensorsMode("camera");
  const sensorLayer = $("#motionsensors");
  if (sensorLayer.length) {
    const cameraFloorSVG = tempCont.find("#plannerSVG").clone();
    cameraFloorSVG.find("foreignObject").remove();
    cameraFloorSVG.attr("width", "100%");
    cameraFloorSVG.find(".planner-notes, .measurements").remove();
    cameraFloorSVG.find(".rooms .poly").css("stroke", "gray");

    const deviceIdsToKeepArr = PlannerGlobals.devices
        .filter((dev: any) => isCamera(dev))
        .map((dev: any) => dev.deviceId);
    deviceIdsToKeepArr.forEach((devId: any) => {
      cameraFloorSVG.find(`[data-deviceid="${devId}"]`).addClass("keep");
      cameraFloorSVG.find(`[data-badgeofdevid="${devId}"]`).addClass("keep");
    });
    cameraFloorSVG.find(".device, #grouppaths > path, .quote-number").not(".keep").remove();
    cameraFloorSVG
        .find(".serial, .serial-connected, .planner-rotatable")
        .each(function (this: any) {
          if ($(this).find(".device").length === 0) {
            $(this).remove();
          }
        });

    const elemToPush = { plannerSVG: cameraFloorSVG, floorId: floorId, isCamera: true };
    const floorIdx = target.plannerSVGs.findIndex(
        (x: any) => x.floorId === floorId && !x.isWifi,
    );
    target.plannerSVGs.splice(floorIdx + 1, 0, elemToPush);

    toggleMotionSensorsMode("camera");
  }
}

function loadWiringMaps(target: OfferSvgData, routingSvgs: string[]): Promise<any> {
  const groups = target.gatheredGroups;
  const svgs = Array.isArray(routingSvgs) ? routingSvgs : [];

  return Promise.all(
      svgs.map((svgStr) => {
        const wiringLayersSVG = $(svgStr);
        const floorId = +wiringLayersSVG.attr("data-floorid")!;
        const wiringLayerGs = wiringLayersSVG.find("g");

        const wiringProms: Promise<void>[] = [];

        wiringLayerGs.each(function (this: any) {
          wiringProms.push(
              new Promise<void>((res) => {
                const wiringLayer = $(this);

                if (!wiringLayer.html() || !wiringLayer.html().length) {
                  return res();
                }

                const floorEntry = target.plannerSVGs.find(
                    (x: any) => x.floorId === floorId,
                );
                if (!floorEntry) {
                  return res();
                }
                const floorSVGClone = floorEntry.plannerSVG.clone();

                floorSVGClone.attr("width", "100%");
                floorSVGClone.find(".planner-notes, .measurements").remove();
                floorSVGClone.find(".rooms .poly").css("stroke", "gray");

                const deviceIdsToKeep = wiringLayer.attr("data-deviceids");
                if (deviceIdsToKeep) {
                  const deviceIdsToKeepArr = deviceIdsToKeep.split(",");

                  groups.forEach((grp: any) => {
                    const foundMatch = grp.deviceIds.some((devId: any) =>
                        deviceIdsToKeepArr.find((x) => `${x}` === `${devId}`),
                    );
                    if (foundMatch) {
                      floorSVGClone.find(`.line_group_${grp.id}`).addClass("keep");
                      grp.deviceIds.forEach((devId: any) => {
                        deviceIdsToKeepArr.push(devId);
                      });
                    }
                  });

                  deviceIdsToKeepArr.forEach((devId) => {
                    floorSVGClone.find(`[data-deviceid="${devId}"]`).addClass("keep");
                    floorSVGClone
                        .find(`[data-badgeofdevid="${devId}"]`)
                        .addClass("keep");
                  });
                  wiringLayer.find(".wire-label").each(function (this: any) {
                    const wireTextDevId = $(this).attr("data-text-deviceid");
                    floorSVGClone
                        .find(`.quote-symbol[data-deviceid="${wireTextDevId}"]`)
                        .remove();
                  });
                  floorSVGClone.find('[id^="S_"]').addClass("keep");
                  floorSVGClone
                      .find(".device, #grouppaths > path, .quote-number, .quote-symbol")
                      .not(".keep")
                      .remove();
                  floorSVGClone
                      .find(".serial, .serial-connected, .planner-rotatable")
                      .each(function (this: any) {
                        if ($(this).find(".device").length === 0) {
                          $(this).remove();
                        }
                      });

                  floorSVGClone.find(".keep").removeClass("keep");
                }

                wiringLayer.removeAttr("data-deviceIds");
                const wiringDId = wiringLayer.attr("data-did");

                if (wiringLayer.find("mask").length) {
                  const wiringLayerConvertSvg = $(makeSVG("svg"));
                  wiringLayerConvertSvg
                      .width(900)
                      .height(900)
                      .attr("viewBox", "0 0 900 900");
                  wiringLayerConvertSvg.append(wiringLayer);

                  svgToRasterImageNode(
                      wiringLayerConvertSvg[0],
                      900,
                      900,
                      4,
                      function (image: any) {
                        if (image) {
                          floorSVGClone.children("g").append(image);
                          target.plannerSVGs.push({
                            plannerSVG: floorSVGClone,
                            floorId: floorId,
                            isWiring: true,
                            wiringType: wiringDId,
                          });
                        }
                        return res();
                      },
                  );
                } else {
                  floorSVGClone.append(wiringLayer);
                  target.plannerSVGs.push({
                    plannerSVG: floorSVGClone,
                    floorId: floorId,
                    isWiring: true,
                    wiringType: wiringDId,
                  });
                  return res();
                }
              }),
          );
        });

        return Promise.all(wiringProms);
      }),
  );
}